'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet, apiPost } from '@/lib/api'
import type { Account, Contact } from '@/types'

type ContactsSyncState = {
  contacts: Contact[]
  syncing: boolean
  syncError: string
  syncContacts: (mode?: 'initial' | 'diff', currentAccount?: Account | null) => Promise<void>
  /** Resolve one uncached wxid through Go and merge it into local contacts. */
  resolveContact: (wxid: string) => Promise<Contact | null>
  /** True while a previous resolve for this wxid is still within its negative-cache window. */
  isKnownUnresolvable: (wxid: string) => boolean
  reset: () => void
}

// Wxids the protocol cannot resolve (members who left a group, deleted friends)
// are remembered for a while so a busy conversation does not re-ask on every
// inbound message. The window is short enough that a genuinely transient
// failure — a gateway hiccup, a wxproto restart — still gets another chance.
const UNRESOLVABLE_TTL_MS = 5 * 60_000

export function useContactsSync(account: Account | null): ContactsSyncState {
  const accountWxid = account?.wxid || ''
  const [contacts, setContacts] = useState<Contact[]>([])
  const [syncing, setSyncing] = useState(false)
  const [syncError, setSyncError] = useState('')
  const initialSyncWxidRef = useRef('')
  const longLinkWxidRef = useRef('')
  // Bumped on sync/account switch so stale responses cannot overwrite contacts.
  const syncGenerationRef = useRef(0)
  // Mirrors the currently selected account for async guards in resolveContact.
  const activeAccountWxidRef = useRef(account?.wxid || '')
  // De-dupe concurrent resolve calls for the same wxid — e.g. a burst of
  // messages from a new sender all firing resolveContact at once.
  const resolvingRef = useRef(new Map<string, Promise<Contact | null>>())
  // wxid -> expiry timestamp of its negative-cache entry.
  const unresolvableRef = useRef(new Map<string, number>())

  const isKnownUnresolvable = useCallback((wxid: string): boolean => {
    const expiresAt = unresolvableRef.current.get(wxid)
    if (expiresAt === undefined) return false
    if (expiresAt > Date.now()) return true
    unresolvableRef.current.delete(wxid)
    return false
  }, [])

  const syncContacts = useCallback(
    async (mode: 'initial' | 'diff' = 'diff', currentAccount: Account | null = account) => {
      if (!currentAccount) return
      const generation = ++syncGenerationRef.current
      setSyncError('')
      setSyncing(true)
      try {
        const response = await apiPost<{
          success: boolean
          contacts: Contact[]
        }>('/contacts/sync', { wxid: currentAccount.wxid, mode })
        if (syncGenerationRef.current !== generation) return
        setContacts(response.contacts || [])
      } catch (error) {
        if (syncGenerationRef.current !== generation) return
        setSyncError(error instanceof Error ? error.message : '通讯录同步失败')
      } finally {
        if (syncGenerationRef.current === generation) setSyncing(false)
      }
    },
    [account],
  )

  const resolveContact = useCallback(
    async (wxid: string): Promise<Contact | null> => {
      if (!account || !wxid) return null
      if (wxid === account.wxid) return null
      if (wxid.startsWith('gh_')) return null

      // Closed-over cache check is safe because callers also gate before resolving.
      if (contacts.some((c) => c.wxid === wxid)) {
        return contacts.find((c) => c.wxid === wxid) ?? null
      }

      const pending = resolvingRef.current.get(wxid)
      if (pending) return pending
      // Without this the callers' `!contactMap.has(wxid)` gate never closes for
      // a wxid the protocol cannot resolve, so every inbound message re-fires
      // the request.
      if (isKnownUnresolvable(wxid)) return null

      const requestAccountWxid = account.wxid
      const promise = (async (): Promise<Contact | null> => {
        try {
          const response = await apiPost<{
            success: boolean
            contacts: Contact[]
            missing?: string[]
          }>(
            '/contacts/resolve',
            { wxid: account.wxid, wxids: [wxid] },
            // Swallow auto-toasts — the caller will silently
            // fall back to the raw wxid as the display name.
            { silent: true },
          )
          const resolved = (response.contacts || []).find((c) => c.wxid === wxid) ?? null
          if (!resolved) unresolvableRef.current.set(wxid, Date.now() + UNRESOLVABLE_TTL_MS)
          // Do not merge a resolve result after the selected account changes.
          if (activeAccountWxidRef.current !== requestAccountWxid) return resolved
          if (resolved) {
            setContacts((current) => {
              const idx = current.findIndex((c) => c.wxid === resolved.wxid)
              if (idx === -1) return [...current, resolved]
              const next = current.slice()
              next[idx] = { ...current[idx], ...resolved }
              return next
            })
          }
          return resolved
        } catch {
          unresolvableRef.current.set(wxid, Date.now() + UNRESOLVABLE_TTL_MS)
          return null
        } finally {
          resolvingRef.current.delete(wxid)
        }
      })()
      resolvingRef.current.set(wxid, promise)
      return promise
    },
    [account, contacts, isKnownUnresolvable],
  )

  const startLongLink = useCallback(async (currentAccount: Account) => {
    if (longLinkWxidRef.current === currentAccount.wxid) return
    longLinkWxidRef.current = currentAccount.wxid
    try {
      const response = await apiPost<{ success: boolean; skipped?: boolean; message?: string }>(
        '/bot/messages/longlink',
        { wxid: currentAccount.wxid },
      )
      if (response.skipped) {
        console.info('长连接未启动', response.message || response)
        return
      }
      if (!response.success) {
        longLinkWxidRef.current = ''
        console.warn('长连接建立失败', response.message || response)
      }
    } catch (error) {
      longLinkWxidRef.current = ''
      console.warn('长连接建立失败', error)
    }
  }, [])

  // Load local contacts when the selected wxid changes.
  useEffect(() => {
    activeAccountWxidRef.current = accountWxid
    // Invalidate any in-flight sync for the previous account.
    syncGenerationRef.current += 1
    // "Unresolvable" is a per-account verdict — the next account may well know this wxid.
    unresolvableRef.current.clear()
    // Drop the previous account's contacts before the request: if it fails we
    // must show nothing rather than the other account's names and avatars.
    setContacts([])
    setSyncError('')
    if (!accountWxid) return
    let cancelled = false
    void apiGet<{
      success: boolean
      contacts: Contact[]
    }>('/contacts?wxid=' + encodeURIComponent(accountWxid))
      .then((res) => {
        if (!cancelled) setContacts(res.contacts || [])
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setContacts([])
        setSyncError(error instanceof Error ? error.message : '通讯录加载失败')
      })
    return () => {
      cancelled = true
    }
  }, [accountWxid])

  // Long-link + initial sync once per wxid; reset refs on logout.
  useEffect(() => {
    if (!account) {
      initialSyncWxidRef.current = ''
      longLinkWxidRef.current = ''
      return
    }
    void startLongLink(account)
    if (initialSyncWxidRef.current === account.wxid) return
    initialSyncWxidRef.current = account.wxid
    void syncContacts('initial', account)
  }, [account, startLongLink, syncContacts])

  const reset = useCallback(() => {
    setContacts([])
    setSyncError('')
    resolvingRef.current.clear()
    unresolvableRef.current.clear()
  }, [])

  return { contacts, syncing, syncError, syncContacts, resolveContact, isKnownUnresolvable, reset }
}
